import React from 'react';
import { rem } from 'polished';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Hidden } from '../../helpers';
import { ImageInterface } from '../../types';
import { spaces } from '../../core/styles/theme/default';
import { DefaultImage, CustomImage } from './partials';

const StyledFigure = styled.figure(
  ({ maxWidth }: { maxWidth?: number }) => css`
    img {
      vertical-align: top;
    }

    ${maxWidth &&
    css`
      .m-richtext & {
        max-width: ${rem(maxWidth)};
        margin: 0 auto;
      }
    `}
  `,
);

const StyledLink = styled.a`
  .m-richtext & {
    display: block;
    text-decoration: none;
    background: none;
  }
`;

const StyledCaption = styled.figcaption`
  text-align: center;
  font-size: ${rem(16)};
  margin-top: ${rem(spaces['delta'])};
  padding: 0 ${rem(spaces['beta'])};
  color: #9c9c9d;

  ${({ theme }) => theme?.stylesExtension?.imageWithCaption?.caption}
`;

const ImageWithCaption = ({
  data: {
    image,
    mobileImage,
    focusPoint,
    mobileFocusPoint,
    caption,
    imageFill,
    imageSizes,
    link,
    maxWidth,
    loading = 'lazy',
    disableLink = false,
    objectFit,
  },
  ...rest
}: ImageWithCaptionProps) => {
  let imageComponent;

  if (image?.gatsbyImageData) {
    imageComponent = (
      <DefaultImage
        imageData={image}
        imageDataMobile={mobileImage}
        imageSizes={imageSizes}
        imageFill={imageFill}
        focusPoint={focusPoint}
        focusPointMobile={mobileFocusPoint}
        caption={caption}
        loading={loading}
        objectFit={objectFit}
      />
    );
  } else if (image?.file.url) {
    imageComponent = mobileImage?.file.url ? (
      <>
        <Hidden above="large">
          <CustomImage
            url={mobileImage.file.url}
            caption={caption}
            loading={loading}
          />
        </Hidden>
        <Hidden below="large">
          <CustomImage
            url={image.file.url}
            caption={caption}
            loading={loading}
          />
        </Hidden>
      </>
    ) : (
      <CustomImage url={image.file.url} caption={caption} loading={loading} />
    );
  } else {
    imageComponent = null;
  }

  return imageComponent ? (
    <StyledFigure maxWidth={maxWidth} {...rest}>
      {!disableLink && link ? (
        <StyledLink
          href={link.url}
          title={link.linkText}
          {...(link.isExternal && {
            target: '_blank',
            rel: 'noreferrer noopener',
          })}
        >
          {imageComponent}
        </StyledLink>
      ) : (
        imageComponent
      )}
      {caption && <StyledCaption>{caption}</StyledCaption>}
    </StyledFigure>
  ) : null;
};

export type ImageWithCaptionProps = {
  data: ImageInterface;
};

export default ImageWithCaption;
